"use client";

import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import toast from 'react-hot-toast';
import { getSubdomain } from '@/lib/subdomain';
import { getApiBaseUrl } from '@/lib/api';

export interface BenefitItem {
    icon: string;
    title: string;
    subtitle: string;
}

export interface BenefitsBarConfig {
    is_active?: boolean;
    items?: BenefitItem[];
}

export interface WhatsAppWidgetConfig {
    is_active?: boolean;
    phone?: string;
    message?: string;
    position?: 'right' | 'left';
    call_to_action?: string;
}

export interface ApiSettings {
    store_name?: string;
    logo_url?: string;
    banner_url?: string;
    hero_banners?: any[];
    benefits_bar?: BenefitsBarConfig;
    whatsapp_widget?: WhatsAppWidgetConfig;
    primary_color?: string;
    secondary_color?: string;
    tertiary_color?: string;
    slogan?: string;
    about?: string;
    shipping_method?: 'disabled' | 'fixed' | 'melhor_envio';
    fixed_shipping_rate?: number;
    instagram_url?: string;
    facebook_url?: string;
    tiktok_url?: string;
    phone?: string;
    email?: string;
    cnpj?: string;
    catalog_type?: 'ecommerce' | 'food';
    top_categories_data?: { id: number; name: string; slug: string; image_url: string | null }[];
    top_bar_text?: string;
    top_bar_is_active?: boolean;
    popup_text?: string;
    popup_is_active?: boolean;
    store_template?: string;
    sports_theme?: any;
    ocultar_depoimento?: boolean;
    ocultar_produtos?: boolean;
    ocultar_reservas?: boolean;
    currency?: string;
    subdomain?: string;
    cep?: string;
    street?: string;
    number?: string;
    complement?: string;
    district?: string;
    city?: string;
    state?: string;
    horario_funcionamento?: any;
}

interface StoreContextType {
    settings: ApiSettings;
    isLoading: boolean;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export const StoreProvider = ({ children }: { children: ReactNode }) => {
    const [settings, setSettings] = useState<ApiSettings>({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchStoreSettings = async () => {
            try {
                const subdomain = getSubdomain();

                if (!subdomain) {
                    toast.error("Não foi possível identificar a loja.");
                    return;
                }

                const apiBaseUrl = getApiBaseUrl();
                const apiHeaders = { 'X-Tenant-Subdomain': subdomain, 'Accept': 'application/json' };
                const response = await fetch(`${apiBaseUrl}/public/settings?subdomain=${encodeURIComponent(subdomain)}&_t=${Date.now()}`, { 
                    headers: apiHeaders,
                    cache: 'no-store'
                });

                if (!response.ok) {
                    throw new Error('Falha ao buscar configurações da loja.');
                }

                const settingsData = await response.json();
                setSettings(settingsData);

            } catch (error) {
                console.error("Erro ao carregar configurações da loja:", error);
                toast.error("Não foi possível carregar as informações da loja.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchStoreSettings();
    }, []);

    return (
        <StoreContext.Provider value={{ settings, isLoading }}>
            {children}
        </StoreContext.Provider>
    );
};

export const useStore = () => {
    const context = useContext(StoreContext);
    if (context === undefined) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return context;
};